import { Link, useNavigate, useParams } from 'react-router';
import {
  addDays,
  cardSummary,
  installmentProgress,
  isOpen,
  loanState,
  payeeDues,
  type Due,
  type PayeeLike,
  type PaymentsSnapshot,
} from '@tahi/core';
import { deletePayee, restorePayee } from '../../../data/commands/payments';
import { useCommandEnv } from '../../../data/hooks';
import { Button, Money, ScreenHeader, showToast } from '../../../ui';
import { cn } from '../../../ui/cn';
import { CARD, FOCUS, LABEL, QUIET_LINK, SCREEN } from '../../spending/styles';
import { payPath } from '../components/DueRow';
import { PayoffChart } from '../components/PayoffChart';
import { copy } from '../copy';
import {
  dayShort,
  interestText,
  kindLabel,
  monthShort,
  partialText,
  peso,
  scheduleText,
  whenText,
} from '../format';
import { PAYMENTS_BASE, useInstallmentCatchUp, usePaymentsSnapshot, useToday } from '../hooks';
import { daysBetween } from '@tahi/core';

const BACK = { to: PAYMENTS_BASE, label: copy.title };

function statusText(d: Due): string {
  switch (d.status) {
    case 'paid':
      return d.amountMinor > 0 ? `Paid ${peso(d.paidMinor)}` : 'Nothing owed';
    case 'prepaid':
      return 'Before Tahi';
    case 'partial':
      return partialText(d);
    case 'skipped':
      return 'Skipped';
    case 'closed':
      return `Closed · paid ${peso(d.paidMinor)}`;
    case 'rolled':
      return copy.rolled;
    case 'open':
      return d.estimated ? copy.estimated : 'Not paid yet';
  }
}

function DueLine({ due, today }: { due: Due; today: string }) {
  const late = isOpen(due) && due.dueDate < today;
  return (
    <li>
      <Link
        to={payPath(due.payeeId, due.period)}
        className={cn(
          'flex min-h-12 items-center justify-between gap-3 rounded-2xl px-2 py-1 hover:bg-secondary',
          FOCUS,
        )}
      >
        <span className="flex min-w-0 flex-col">
          <span className="text-fg">{dayShort(due.dueDate)}</span>
          <span className={cn('text-sm', late ? 'text-destructive' : 'text-muted')}>
            {isOpen(due) ? `${whenText(daysBetween(today, due.dueDate))} · ` : ''}
            {statusText(due)}
          </span>
        </span>
        <Money
          minor={due.amountMinor}
          cents="auto"
          animate={false}
          className="shrink-0 text-base text-fg"
        />
      </Link>
    </li>
  );
}

function Stat({ label, minor, sub }: { label: string; minor: number; sub?: string }) {
  return (
    <div className="flex flex-col">
      <span className={LABEL}>{label}</span>
      <Money minor={minor} cents="auto" className="text-[24px] leading-tight" />
      {sub ? <span className="text-sm text-muted">{sub}</span> : null}
    </div>
  );
}

function DuesCard({ title, dues, today }: { title: string; dues: Due[]; today: string }) {
  if (dues.length === 0) return null;
  return (
    <section aria-label={title} className={`${CARD} flex flex-col gap-1`}>
      <h2 className={LABEL}>{title}</h2>
      <ul className="flex flex-col">
        {dues.map((d) => (
          <DueLine key={d.id} due={d} today={today} />
        ))}
      </ul>
    </section>
  );
}

function ProgressBar({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      className="h-2 w-full overflow-hidden rounded-full bg-secondary"
    >
      <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
    </div>
  );
}

function CardBody({
  snap,
  card,
  today,
}: {
  snap: PaymentsSnapshot;
  card: PayeeLike;
  today: string;
}) {
  const s = cardSummary(snap, card, today);
  const installments = snap.installments.filter((i) => i.cardId === card.id && !i.deletedAt);
  const statements = [...s.statements].reverse().slice(0, 8);
  return (
    <>
      <section className={`${CARD} grid grid-cols-2 gap-4`}>
        <Stat label={copy.balance} minor={s.balanceMinor} />
        {s.availableMinor !== null ? (
          <Stat label={copy.available} minor={s.availableMinor} />
        ) : null}
        <div className="col-span-2 text-sm text-muted">
          {copy.nextStatement} {dayShort(s.nextStatementDate)}
          {s.nextDue
            ? ` · next due ${dayShort(s.nextDue.dueDate)} ${peso(s.nextDue.remainingMinor)}`
            : ''}
        </div>
        {card.cardholder ? (
          <p className="col-span-2 text-sm text-muted">{copy.supplementary(card.cardholder)}</p>
        ) : null}
      </section>
      <section aria-label={copy.installments} className={`${CARD} flex flex-col gap-3`}>
        <div className="flex items-center justify-between gap-2">
          <h2 className={LABEL}>{copy.installments}</h2>
          <Link to={`${PAYMENTS_BASE}/p/${card.id}/installments/new`} className={QUIET_LINK}>
            + {copy.addInstallment}
          </Link>
        </div>
        {installments.length === 0 ? (
          <p className="text-sm text-muted">{copy.noInstallments}</p>
        ) : null}
        <ul className="flex flex-col gap-3">
          {installments.map((i) => {
            const pr = installmentProgress(i, card, today);
            return (
              <li key={i.id}>
                <Link
                  to={`${PAYMENTS_BASE}/p/${card.id}/installments/${i.id}`}
                  className={cn(
                    'flex flex-col gap-1 rounded-2xl px-2 py-2 hover:bg-secondary',
                    FOCUS,
                  )}
                >
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-fg">{i.item}</span>
                    <span className="shrink-0 font-mono text-sm tabular-nums text-fg">
                      {copy.monthlyFor(i.monthlyMinor, i.months)}
                    </span>
                  </span>
                  <ProgressBar
                    value={pr.monthsPaid}
                    max={i.months}
                    label={`${i.item}: ${copy.monthsOf(pr.monthsPaid, i.months)}`}
                  />
                  <span className="text-sm text-muted">
                    {copy.monthsOf(pr.monthsPaid, i.months)} · {copy.leftOn(pr.remainingMinor)} ·{' '}
                    {monthShort(i.startMonth)} – {monthShort(pr.endMonth)}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>
      <DuesCard
        title={copy.statements}
        dues={statements.filter((d) => d.amountMinor > 0)}
        today={today}
      />
    </>
  );
}

function LoanBody({
  snap,
  loan,
  today,
}: {
  snap: PaymentsSnapshot;
  loan: PayeeLike;
  today: string;
}) {
  const st = loanState(snap, loan, today);
  const dues = payeeDues(snap, loan, addDays(today, 62), today);
  const upcoming = dues.filter((d) => isOpen(d)).slice(0, 4);
  const past = dues
    .filter((d) => !isOpen(d) && d.status !== 'prepaid')
    .reverse()
    .slice(0, 8);
  const extra = snap.payments.filter((p) => p.payeeId === loan.id && !p.dueId && !p.deletedAt);
  const flexible = st.type === 'flexible';
  return (
    <>
      <section className={`${CARD} grid grid-cols-2 gap-4`}>
        <Stat label={copy.loanLeft} minor={st.balanceMinor} />
        <Stat label={copy.paidSoFar} minor={st.paidMinor} />
        <p className="col-span-2 text-sm text-muted">
          {[
            loan.lender !== loan.name ? loan.lender : null,
            flexible ? interestText(loan.interestBp) : null,
            st.paymentsLeft !== null ? copy.paymentsLeft(st.paymentsLeft) : null,
            st.payoffDate
              ? `${copy.payoff} ${dayShort(st.payoffDate)} ${st.payoffDate.slice(0, 4)}`
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        {flexible && st.interestMinor > 0 ? (
          <p className="col-span-2 text-sm text-muted">{copy.interestSoFar(st.interestMinor)}</p>
        ) : null}
        {flexible &&
        st.payoffDate === null &&
        st.balanceMinor > 0 &&
        (loan.amountMinor ?? 0) > 0 ? (
          <p className="col-span-2 text-sm text-muted">{copy.payoffNever}</p>
        ) : null}
        <div className="col-span-2">
          <PayoffChart points={st.points} ariaLabel={`${loan.name} balance over time`} />
        </div>
        {flexible ? (
          <Link
            to={`${PAYMENTS_BASE}/p/${loan.id}/pay`}
            className={`col-span-2 inline-flex min-h-11 items-center justify-center rounded-full bg-accent px-5 font-medium text-on-accent ${FOCUS}`}
          >
            {copy.payAnyAmount}
          </Link>
        ) : null}
      </section>
      <DuesCard title="Coming up" dues={upcoming} today={today} />
      {extra.length > 0 ? (
        <section aria-label={copy.payments} className={`${CARD} flex flex-col gap-1`}>
          <h2 className={LABEL}>{copy.payments}</h2>
          <ul className="flex flex-col">
            {extra.map((p) => (
              <li key={p.id} className="flex min-h-11 items-center justify-between text-sm">
                <span className="text-fg">{dayShort(p.paidDate)}</span>
                <span className="font-mono tabular-nums text-fg">{peso(p.amountMinor)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <DuesCard title={copy.history} dues={past} today={today} />
    </>
  );
}

function BillBody({
  snap,
  payee,
  today,
}: {
  snap: PaymentsSnapshot;
  payee: PayeeLike;
  today: string;
}) {
  const dues = payeeDues(snap, payee, addDays(today, 62), today);
  const upcoming = dues.filter((d) => isOpen(d)).slice(0, 4);
  const past = dues
    .filter((d) => !isOpen(d) && d.status !== 'prepaid')
    .reverse()
    .slice(0, 12);
  return (
    <>
      <DuesCard title="Coming up" dues={upcoming} today={today} />
      <DuesCard title={copy.history} dues={past} today={today} />
      {past.length === 0 ? <p className="text-sm text-muted">{copy.noHistory}</p> : null}
    </>
  );
}

export function PayeeDetailScreen() {
  const { id = '' } = useParams();
  const snap = usePaymentsSnapshot();
  const today = useToday();
  const env = useCommandEnv();
  const navigate = useNavigate();
  useInstallmentCatchUp();
  if (!snap) return <p className="text-muted">{copy.loading}</p>;
  const payee = snap.payees.find((p) => p.id === id);
  if (!payee) {
    return (
      <div className={SCREEN}>
        <ScreenHeader title={copy.title} back={BACK} />
        <p className="text-muted">{copy.notFound}</p>
      </div>
    );
  }

  async function remove() {
    const r = await deletePayee(env, payee!.id);
    if (!r.ok) return;
    showToast({
      message: copy.deletedToast(payee!.name),
      action: {
        label: copy.undo,
        onPress: () => void restorePayee(env, payee!.id, r.installmentIds),
      },
    });
    navigate(PAYMENTS_BASE, { replace: true });
  }

  return (
    <div className={SCREEN}>
      <div className="flex flex-col gap-1">
        <ScreenHeader
          title={payee.name}
          back={BACK}
          actions={
            <Link to={`${PAYMENTS_BASE}/p/${payee.id}/edit`} className={QUIET_LINK}>
              {copy.edit}
            </Link>
          }
        />
        <p className="text-sm text-muted">
          {kindLabel[payee.kind]} · {scheduleText(payee)}
          {payee.autoPay ? ' · auto-debit' : ''}
        </p>
      </div>
      {payee.kind === 'card' ? <CardBody snap={snap} card={payee} today={today} /> : null}
      {payee.kind === 'loan' ? <LoanBody snap={snap} loan={payee} today={today} /> : null}
      {payee.kind === 'bill' || payee.kind === 'utility' ? (
        <BillBody snap={snap} payee={payee} today={today} />
      ) : null}
      {payee.note ? <p className="text-sm text-muted">{payee.note}</p> : null}
      <Button variant="quiet-destructive" className="self-start" onClick={() => void remove()}>
        {copy.delete}
      </Button>
    </div>
  );
}
